/** List View sorting/grouping preferences. Persisted per-user via backend API. */

export type ListViewGroupBy =
  | 'none'
  | 'status'
  | 'assignee'
  | 'priority'
  | 'tags'
  | 'dueDate'
  | 'list'
  | 'lastUpdated';

export type ListViewSortDirection = 'asc' | 'desc';

/** Column sort for the list table (within each group); independent of Group by / toolbar sort. */
export type ListViewTableSortColumn =
  | 'name'
  | 'status'
  | 'assignee'
  | 'dueDate'
  | 'priority'
  | 'updatedAt';

export interface ListViewPreferences {
  groupBy: ListViewGroupBy;
  sortDirection: ListViewSortDirection;
  alsoGroupByList: boolean;
  /** Custom field definition IDs to show as columns (legacy whitelist mode) */
  visibleCustomFieldIds: string[];
  /** Custom field definition IDs explicitly hidden by the user */
  hiddenCustomFieldIds?: string[];
  /** When set, tasks within each list group are ordered by this column (client-side). */
  tableSortColumn: ListViewTableSortColumn | null;
  tableSortDirection: ListViewSortDirection;
  /** Group keys that are collapsed in the UI. */
  collapsedGroups: string[];
  /** When true, show tasks in done/closed statuses. Default false (hide closed). */
  showClosedTasks?: boolean;
}

export const DEFAULT_LIST_VIEW_PREFS: ListViewPreferences = {
  groupBy: 'status',
  sortDirection: 'asc',
  alsoGroupByList: false,
  visibleCustomFieldIds: [],
  hiddenCustomFieldIds: [],
  tableSortColumn: null,
  tableSortDirection: 'asc',
  collapsedGroups: [],
  showClosedTasks: false,
};

export function parseListViewPreferences(raw: unknown): ListViewPreferences {
  const parsed = (raw ?? {}) as Partial<ListViewPreferences>;
  const ids = Array.isArray(parsed.visibleCustomFieldIds)
    ? parsed.visibleCustomFieldIds.filter((id): id is string => typeof id === 'string')
    : [];
  const hiddenIds = Array.isArray(parsed.hiddenCustomFieldIds)
    ? parsed.hiddenCustomFieldIds.filter((id): id is string => typeof id === 'string')
    : [];
  const collapsedGroups = Array.isArray(parsed.collapsedGroups)
    ? parsed.collapsedGroups.filter((g): g is string => typeof g === 'string')
    : [];

  return {
    groupBy: validGroupBy(parsed.groupBy),
    sortDirection: parsed.sortDirection === 'desc' ? 'desc' : 'asc',
    alsoGroupByList: Boolean(parsed.alsoGroupByList),
    visibleCustomFieldIds: ids,
    hiddenCustomFieldIds: hiddenIds,
    tableSortColumn: validTableSortColumn(parsed.tableSortColumn),
    tableSortDirection: parsed.tableSortDirection === 'desc' ? 'desc' : 'asc',
    collapsedGroups,
    showClosedTasks: Boolean(parsed.showClosedTasks),
  };
}

function validGroupBy(value: unknown): ListViewGroupBy {
  const valid: ListViewGroupBy[] = [
    'none',
    'status',
    'assignee',
    'priority',
    'tags',
    'dueDate',
    'list',
    'lastUpdated',
  ];
  if (typeof value === 'string' && valid.includes(value as ListViewGroupBy)) {
    return value as ListViewGroupBy;
  }
  return 'status';
}

const TABLE_SORT_COLUMNS: ListViewTableSortColumn[] = [
  'name',
  'status',
  'assignee',
  'dueDate',
  'priority',
  'updatedAt',
];

function validTableSortColumn(value: unknown): ListViewTableSortColumn | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && TABLE_SORT_COLUMNS.includes(value as ListViewTableSortColumn)) {
    return value as ListViewTableSortColumn;
  }
  return null;
}
